import { useCallback, useMemo } from "react";

import {
  OptionSelector,
  OptionSelectorTrigger,
} from "#app/components/custom-fields/option-picker";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { Omittable } from "@salestrak/ui";

type CustomFieldOptionInputProps = Omittable<{
  field: CustomFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
}>;

/**
 * Advanced-row value editor for the option field types (Single/Multi Select).
 * Reuses the real value picker in pick-only trim — no inline create, no manage
 * affordances — and always multi: the operators are membership tests
 * (hasSome/hasNone), so the operand is a value array for both types.
 */
export function CustomFieldOptionInput({
  field,
  value,
  onChange,
}: CustomFieldOptionInputProps) {
  const values = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value],
  );

  const handleChange = useCallback(
    (next: string[]) => onChange(next.length > 0 ? next : null),
    [onChange],
  );

  return (
    <OptionSelector
      field={field}
      values={values}
      multiple
      onValuesChange={handleChange}
    >
      <OptionSelectorTrigger
        placeholder="Select options..."
        size="sm"
        chips
        fill
      />
    </OptionSelector>
  );
}
